
import { db, storage } from '../firebase';
import { handleFirestoreError, OperationType } from '../utils/firebaseErrors';
// FIX: Added 'Font' to the type import to support the new mock fontService.
import type { Prompt, UserPromptFeedback, Collection, ExampleMedia, Font, PromptVersion } from '../types';
import { 
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, 
  query, where, onSnapshot, orderBy, serverTimestamp, increment, runTransaction, 
  Timestamp, addDoc, getDocFromServer, collectionGroup
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { optimizeImageToDataUrl } from '../utils/imageOptimization';

const promptsCollection = collection(db, 'prompts');
const collectionsCollection = collection(db, 'collections');
const feedbackCollection = collection(db, 'feedback');

/**
 * Converts a Firestore document snapshot into a Prompt object,
 * handling timestamp conversions safely.
 */
const docToPrompt = (docSnap: any): Prompt => {
  const data = docSnap.data();
  
  // Safe date conversion helper
  const toDate = (val: any) => {
    if (!val) return new Date();
    if (val.toDate) return val.toDate(); // Firestore Timestamp
    if (val instanceof Date) return val;
    return new Date(val); // String or number
  };

  return {
    id: docSnap.id,
    ...data,
    // Add default values to prevent undefined errors in UI
    modalities: data.modalities || [],
    bestWithLLMs: data.bestWithLLMs || [],
    bestWithImageGenerators: data.bestWithImageGenerators || [],
    tags: data.tags || [],
    exampleMedia: data.exampleMedia || [],
    likesCount: data.likesCount || 0,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
};

export const promptService = {
  // --- Real-time Listeners ---
  listenToUserPrompts: (userId: string, callback: (prompts: Prompt[]) => void, onError: (error: Error) => void): (() => void) => {
    // Removed orderBy to avoid requiring a composite index. Sorting is done client-side.
    const q = query(promptsCollection, where('ownerId', '==', userId));
    const unsubscribe = onSnapshot(q, snapshot => {
      const prompts = snapshot.docs.map(docToPrompt);
      prompts.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      callback(prompts);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'prompts');
    });
    return unsubscribe;
  },

  listenToPublicPrompts: (callback: (prompts: Prompt[]) => void, onError: (error: Error) => void): (() => void) => {
    // Removed orderBy to avoid requiring a composite index. Sorting is done client-side.
    const q = query(promptsCollection, where('public', '==', true));
    const unsubscribe = onSnapshot(q, snapshot => {
      const prompts = snapshot.docs.map(docToPrompt);
      prompts.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      callback(prompts);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'prompts');
    });
    return unsubscribe;
  },
  
  listenToUserCollections: (userId: string, callback: (collections: Collection[]) => void, onError: (error: Error) => void): (() => void) => {
    const q = query(collectionsCollection, where('ownerId', '==', userId), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, snapshot => {
        const collections = snapshot.docs.map(docSnap => {
            const data = docSnap.data();
            return {
                id: docSnap.id,
                ...data,
                createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            } as Collection;
        });
        callback(collections);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'collections');
    });
    return unsubscribe;
  },

  // --- One-time Fetches (kept for specific use cases like detail page) ---
  getPublicPrompts: async (): Promise<Prompt[]> => {
    try {
      // Client-side sort
      const q = query(promptsCollection, where('public', '==', true));
      const snapshot = await getDocs(q);
      const prompts = snapshot.docs.map(docToPrompt);
      return prompts.sort((a: Prompt, b: Prompt) => b.createdAt.getTime() - a.createdAt.getTime());
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'prompts');
      return [];
    }
  },

  getUserPrompts: async (userId: string): Promise<Prompt[]> => {
    try {
      // Client-side sort
      const q = query(promptsCollection, where('ownerId', '==', userId));
      const snapshot = await getDocs(q);
      const prompts = snapshot.docs.map(docToPrompt);
      return prompts.sort((a: Prompt, b: Prompt) => b.createdAt.getTime() - a.createdAt.getTime());
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'prompts');
      return [];
    }
  },

  getPublicUserPrompts: async (userId: string): Promise<Prompt[]> => {
    try {
      const q = query(promptsCollection, where('ownerId', '==', userId), where('public', '==', true));
      const snapshot = await getDocs(q);
      const prompts = snapshot.docs.map(docToPrompt);
      return prompts.sort((a: Prompt, b: Prompt) => b.createdAt.getTime() - a.createdAt.getTime());
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'prompts');
      return [];
    }
  },

  listenToUserFavoritePrompts: (userId: string, callback: (prompts: Prompt[]) => void, onError: (error: Error) => void): (() => void) => {
    const favsRef = collection(db, 'user_favorites', userId, 'favorites');
    const unsubscribe = onSnapshot(favsRef, async (snapshot) => {
      try {
        const prompts: Prompt[] = [];
        for (const docSnap of snapshot.docs) {
          const promptId = docSnap.id;
          const p = await promptService.getPromptById(promptId);
          if (p) {
            prompts.push(p);
          }
        }
        prompts.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        callback(prompts);
      } catch (error) {
        if (error instanceof Error) onError(error);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'user_favorites');
    });
    return unsubscribe;
  },

  getPromptById: async (id: string): Promise<Prompt | undefined> => {
    try {
      const docSnap = await getDoc(doc(db, 'prompts', id));
      return docSnap.exists() ? docToPrompt(docSnap) : undefined;
    } catch (error) {
      console.error(`Error fetching prompt ${id}:`, error);
      return undefined;
    }
  },

  savePrompt: async (promptData: Partial<Prompt>, isNew: boolean): Promise<Prompt> => {
    const ts = serverTimestamp();
    const { id, ...data } = promptData;

    if (!id) throw new Error("An ID must be provided to save a prompt.");

    const docRef = doc(db, 'prompts', id);
    const versionsRef = collection(docRef, 'versions');

    // Optimistic return object so UI updates immediately without waiting for a re-fetch
    const now = new Date();

    try {
      if (isNew) {
        // Create new document
        await setDoc(docRef, {
          ...data,
          likesCount: data.likesCount || 0,
          createdAt: ts,
          updatedAt: ts,
        });

        // Save first version
        await addDoc(versionsRef, {
          promptId: id,
          ownerId: data.ownerId,
          title: data.title || '',
          shortDescription: data.shortDescription || '',
          fullPrompt: data.fullPrompt || '',
          updatedAt: ts,
        });

        return {
          id,
          ...data,
          likesCount: 0,
          createdAt: now,
          updatedAt: now,
        } as Prompt;
      } else {
        // Update existing document
        await updateDoc(docRef, { 
          ...data, 
          updatedAt: ts 
        });

        // Save new version
        await addDoc(versionsRef, {
          promptId: id,
          ownerId: data.ownerId,
          title: data.title || '',
          shortDescription: data.shortDescription || '',
          fullPrompt: data.fullPrompt || '',
          updatedAt: ts,
        });

        return {
          id,
          ...data,
          updatedAt: now,
        } as Prompt;
      }
    } catch (error) {
      handleFirestoreError(error, isNew ? OperationType.CREATE : OperationType.UPDATE, `prompts/${id}`);
      throw error;
    }
  },
  
  getPromptVersions: async (promptId: string): Promise<PromptVersion[]> => {
    try {
      const versionsRef = collection(db, 'prompts', promptId, 'versions');
      const q = query(versionsRef, orderBy('updatedAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          promptId: data.promptId,
          title: data.title,
          shortDescription: data.shortDescription,
          fullPrompt: data.fullPrompt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(),
        } as PromptVersion;
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, `prompts/${promptId}/versions`);
      return [];
    }
  },

  updatePrompt: async (promptId: string, dataToUpdate: Partial<Prompt>): Promise<void> => {
      await updateDoc(doc(db, 'prompts', promptId), {
        ...dataToUpdate,
        updatedAt: serverTimestamp(),
      });
  },

  deletePrompt: async (id: string): Promise<void> => {
    await deleteDoc(doc(db, 'prompts', id));
  },

  getFeedbackForPrompt: async (promptId: string): Promise<UserPromptFeedback[]> => {
    const q = query(feedbackCollection, where('promptId', '==', promptId), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
            id: docSnap.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
        } as UserPromptFeedback;
    });
  },

  submitFeedback: async (feedback: Omit<UserPromptFeedback, 'id' | 'createdAt'>): Promise<UserPromptFeedback> => {
    let newFeedbackId = '';
    await runTransaction(db, async (transaction) => {
        const promptRef = doc(db, 'prompts', feedback.promptId);
        const promptDoc = await transaction.get(promptRef);
        
        if (!promptDoc.exists()) {
            throw new Error("Prompt does not exist!");
        }

        const promptData = promptDoc.data();
        const currentRatingCount = promptData.ratingCount || 0;
        const currentAverageRating = promptData.averageRating || 0;

        const newRatingCount = currentRatingCount + 1;
        const newAverageRating = ((currentAverageRating * currentRatingCount) + feedback.rating) / newRatingCount;

        const feedbackRef = doc(feedbackCollection);
        newFeedbackId = feedbackRef.id;

        const newFeedbackData = {
            ...feedback,
            createdAt: serverTimestamp(),
        };

        transaction.set(feedbackRef, newFeedbackData);
        transaction.update(promptRef, {
            ratingCount: newRatingCount,
            averageRating: newAverageRating
        });
    });

    return {
      ...feedback,
      id: newFeedbackId,
      createdAt: new Date(),
    };
  },

  getUserCollections: async (userId: string): Promise<Collection[]> => {
    const q = query(collectionsCollection, where('ownerId', '==', userId), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
            id: docSnap.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
        } as Collection;
    });
  },

  createCollection: async (name: string, ownerId: string): Promise<Collection> => {
    const collectionToSave = {
        name,
        ownerId,
        createdAt: serverTimestamp(),
    };
    const docRef = await addDoc(collectionsCollection, collectionToSave);
    return { id: docRef.id, name, ownerId, createdAt: new Date() };
  },

  updateCollection: async (collectionId: string, name: string): Promise<void> => {
    await updateDoc(doc(db, 'collections', collectionId), {
      name,
    });
  },

  deleteCollection: async (collectionId: string): Promise<void> => {
    await deleteDoc(doc(db, 'collections', collectionId));
  },

  checkIfUserLikedPrompt: async (promptId: string, userId: string): Promise<boolean> => {
      const likeDoc = await getDoc(doc(db, 'prompts', promptId, 'likes', userId));
      return likeDoc.exists();
  },
  
  toggleLikePrompt: async (promptId: string, userId: string): Promise<void> => {
    const promptRef = doc(db, 'prompts', promptId);
    const likeRef = doc(db, 'prompts', promptId, 'likes', userId);
    const userFavoriteRef = doc(db, 'user_favorites', userId, 'favorites', promptId);

    try {
      await runTransaction(db, async (transaction) => {
        const likeDoc = await transaction.get(likeRef);
        if (likeDoc.exists()) {
          // User has liked, so unlike.
          transaction.delete(likeRef);
          transaction.delete(userFavoriteRef);
          transaction.update(promptRef, {
            likesCount: increment(-1),
          });
        } else {
          // User has not liked, so like.
          transaction.set(likeRef, { userId: userId, createdAt: serverTimestamp() });
          transaction.set(userFavoriteRef, { promptId: promptId, createdAt: serverTimestamp() });
          transaction.update(promptRef, {
            likesCount: increment(1),
          });
        }
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `prompts/${promptId}/likes/${userId}`);
    }
  },

  updateUserPromptsOwnerInfo: async (userId: string, newOwnerName: string): Promise<void> => {
    try {
      const q = query(promptsCollection, where('ownerId', '==', userId));
      const snapshot = await getDocs(q);
      const batchPromises = snapshot.docs.map(docSnap => {
        const docRef = doc(db, 'prompts', docSnap.id);
        return updateDoc(docRef, { ownerName: newOwnerName });
      });
      await Promise.all(batchPromises);
    } catch (error) {
      console.error('Error updating user prompts owner info:', error);
    }
  },
};

/**
 * Validates connection to Firestore.
 */
async function testConnection() {
  try {
    const docRef = doc(db, 'test', 'connection');
    await getDocFromServer(docRef);
  } catch (error) {
    if (error instanceof Error && error.message.includes('the client is offline')) {
      console.error("Please check your Firebase configuration. The client is offline.");
    }
  }
}
testConnection();

export const storageService = {
  uploadThumbnail: async (promptId: string, fileOrData: File | string): Promise<string> => {
    try {
      const optimized = await optimizeImageToDataUrl(fileOrData, {
        maxWidth: 1000,
        maxHeight: 1000,
        quality: 0.85,
        mimeType: 'image/jpeg',
      });
      return optimized;
    } catch (error) {
      console.warn("Optimizing thumbnail failed, falling back:", error);
      if (typeof fileOrData === 'string') return fileOrData;
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => resolve('');
        reader.readAsDataURL(fileOrData);
      });
    }
  },

  uploadExampleMedia: async (promptId: string, file: File): Promise<ExampleMedia> => {
    let type: "image" | "video" | "other" = "other";
    if (file.type.startsWith('image/')) {
      type = 'image';
    } else if (file.type.startsWith('video/')) {
      type = 'video';
    }

    // For images, optimize directly to avoid CORS issues with cloud storage
    if (type === 'image') {
      try {
        const fileUrl = await optimizeImageToDataUrl(file, {
          maxWidth: 1200,
          maxHeight: 1200,
          quality: 0.85,
          mimeType: 'image/jpeg',
        });
        return {
          fileUrl,
          type,
          label: file.name,
        };
      } catch (err) {
        console.warn("Optimizing example image failed, using FileReader fallback:", err);
        const fileUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => resolve('');
          reader.readAsDataURL(file);
        });
        return {
          fileUrl,
          type,
          label: file.name,
        };
      }
    }

    // For non-images or videos, attempt storage upload
    try {
      const uniqueFileName = `${Date.now()}-${file.name.replace(/\s+/g, '_')}`;
      const filePath = `prompts/${promptId}/examples/${uniqueFileName}`;
      const storageRef = ref(storage, filePath);
      const snapshot = await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(snapshot.ref);
      return {
        fileUrl,
        type,
        label: file.name
      };
    } catch (err) {
      console.warn("Cloud storage upload failed for media file, using local preview URL:", err);
      const fileUrl = URL.createObjectURL(file);
      return {
        fileUrl,
        type,
        label: file.name
      };
    }
  },

  deleteExampleMedia: async (fileUrl: string): Promise<void> => {
    if (!fileUrl || fileUrl.startsWith('data:') || fileUrl.startsWith('blob:')) {
      return;
    }
    try {
      const storageRef = ref(storage, fileUrl);
      await deleteObject(storageRef);
    } catch (error: any) {
      if (error.code !== 'storage/object-not-found') {
        console.warn("Notice: Example media storage cleanup skipped:", error?.message || error);
      }
    }
  },
};

// FIX: Add mock fontService to resolve error in pages/FontsPage.tsx
export const fontService = {
    getUserFonts: async (userId: string): Promise<Font[]> => {
      // Mock implementation as the feature seems deprecated/incomplete.
      console.warn(`fontService.getUserFonts is a mock implementation for user: ${userId}`);
      return [];
    },
};
